<template>
  <div class="customise-overlay-line-style">
    <LineStyleEditor
      :color="prefs.color"
      :thickness="prefs.thickness"
      :opacity="prefs.opacity"
      color-label="Line colour"
      thickness-label="Line thickness"
      opacity-label="Line opacity"
      @update:color="setColor"
      @update:thickness="setThickness"
      @update:opacity="setOpacity"
      @reset="resetToDefault"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import emitter from '../../../utils/bus';
import LineStyleEditor from './LineStyleEditor.vue';
import {
  DEFAULT_OVERLAY_LINE_STYLES,
  getOverlayLineStyles,
  OverlayLineStyles,
  OverlayLineType,
  saveOverlayLineStyles,
  withOverlayLineStyleReset,
} from '../../../utils/overlayLineStyles';
import { trackOverlayLineStyleChanged } from '../../../utils/overlayLineStyleAnalytics';

/**
 * Edits the line style of one map overlay (official hikes / my hikes /
 * recordings / completed). Reached from the gear on that overlay's card, so it
 * always edits a single overlay — the one whose gear was tapped.
 */
export default defineComponent({
  name: 'CustomiseOverlayLineStyleView',

  components: { LineStyleEditor },

  props: {
    overlayType: { type: String as PropType<OverlayLineType>, required: true },
  },

  data() {
    return {
      styles: getOverlayLineStyles() as OverlayLineStyles,
      onCloudHydrate: (() => {}) as (() => void),
    };
  },

  computed: {
    prefs() {
      return this.styles[this.overlayType] ?? DEFAULT_OVERLAY_LINE_STYLES[this.overlayType];
    },
  },

  // Pick up values hydrated from the server (including ones pushed from
  // iOS/Android) without needing a page reload.
  created() {
    this.onCloudHydrate = () => { this.styles = getOverlayLineStyles(); };
    emitter.on('overlay-line-styles:changed', this.onCloudHydrate);
  },
  beforeUnmount() {
    emitter.off('overlay-line-styles:changed', this.onCloudHydrate);
  },

  methods: {
    setColor(color: string) {
      this.styles[this.overlayType].color = color;
      this.persist('color', color);
    },
    setThickness(value: number) {
      this.styles[this.overlayType].thickness = value;
      this.persist('thickness', value);
    },
    setOpacity(value: number) {
      this.styles[this.overlayType].opacity = value;
      this.persist('opacity', value);
    },
    resetToDefault() {
      this.styles = withOverlayLineStyleReset(this.styles, this.overlayType);
      this.persist('reset', null);
    },
    persist(property: string, value: string | number | null) {
      saveOverlayLineStyles(this.styles);
      // Re-paints the live map layers and schedules the debounced UpdateUser.
      emitter.emit('overlay-line-styles:changed', { ...this.styles });
      trackOverlayLineStyleChanged(this.overlayType, property, value);
    },
  },
});
</script>

<style scoped>
.customise-overlay-line-style {
  padding: 16px;
  padding-bottom: 24px;
}
</style>
